import type {
  IConfigurationBridge,
  IConfigurationContentData,
  IConfigurationVlanBridgePort,
} from 'src/types/configuration';

import { RESERVED_AUTO_DEPLOY_VLAN } from './import-topology-shared';

const usableSm = (sm: string | undefined) => {
  const s = sm?.trim();
  return !!s && s !== '0.0.0.0';
};

export function buildBridgeByVidMap(
  config: IConfigurationContentData,
): Map<number, IConfigurationBridge> {
  const map = new Map<number, IConfigurationBridge>();
  (config.vlan?.bridges ?? []).forEach((b) => {
    map.set(b.vid, b);
  });
  const list = (config.vlan?.bridge as any)?.vlan_list;
  if (!Array.isArray(list)) return map;

  list.forEach((row) => {
    const vid = row.vlan_id;
    if (
      typeof vid !== 'number' ||
      vid === RESERVED_AUTO_DEPLOY_VLAN ||
      map.has(vid)
    ) {
      return;
    }
    if (!usableSm(row.sm)) return;

    map.set(vid, {
      forward: row.forward ?? false,
      gw: row.gw ?? '',
      ip: row.ip ?? '',
      proxy_arp: row.proxy_arp ?? false,
      sm: row.sm!.trim(),
      vid,
      wan: false,
    });
  });
  return map;
}

/** Fill VLAN membership from `vlan.bridge.ports` when `vids[].members` is incomplete (e.g. K500). */
export function mergeVlanMembershipFromBridgePorts(
  vlanIdsByPort: Map<number, number[]>,
  untaggedByPort: Map<number, number | null>,
  ports: Record<string, IConfigurationVlanBridgePort> | undefined,
  portIndexes: Record<string, number>,
): void {
  if (!ports) return;

  Object.entries(ports).forEach(([portKey, portCfg]) => {
    const ifaceIdx = portIndexes[portKey];
    if (typeof ifaceIdx !== 'number' || ifaceIdx <= 0) return;

    const vm = portCfg.vlan_map;
    if (!vm || !Object.keys(vm).length) return;

    const merged = new Set(vlanIdsByPort.get(ifaceIdx) ?? []);
    Object.keys(vm).forEach((key) => {
      const vid = Number(key);
      if (Number.isFinite(vid)) merged.add(vid);
    });
    vlanIdsByPort.set(
      ifaceIdx,
      [...merged].sort((a, b) => a - b),
    );

    const untaggedEntry = Object.entries(vm).find(
      ([, egress]) => egress === 'untagged',
    );
    if (untaggedEntry) {
      const vid = Number(untaggedEntry[0]);
      if (Number.isFinite(vid)) untaggedByPort.set(ifaceIdx, vid);
    }
  });
}
